import React, { useState, useRef } from "react";
import AppImage from "../../../../components/AppImage";
import Icon from "../../../../components/AppIcon";
import Button from "../../../../components/ui/Button";

/* ---------- IMAGE COMPRESSION UTILITY (FIXED) ---------- */
const compressImage = (file, maxWidth = 512, quality = 0.7) => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      // ⚠️ IMPORTANT FIX: use window.Image, NOT AppImage
      const img = new window.Image();
      img.src = event.target.result;

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);

        const compressedBase64 = canvas.toDataURL("image/jpeg", quality);
        resolve(compressedBase64);
      };
    };

    reader.readAsDataURL(file);
  });
};

const ProfilePhotoUpload = ({ currentPhoto, currentPhotoAlt, onPhotoChange }) => {
  const [previewUrl, setPreviewUrl] = useState(currentPhoto);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (file && file.type.startsWith("image/")) {
      const compressedImage = await compressImage(file);
      setPreviewUrl(compressedImage);
      onPhotoChange?.(compressedImage);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files?.[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleFileInputChange = (e) => {
    handleFileSelect(e.target.files?.[0]);
  };

  const handleRemovePhoto = () => {
    setPreviewUrl(null);
    onPhotoChange?.(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-6">
        <div className="relative">
          <div className="w-32 h-32 rounded-full overflow-hidden bg-muted border">
            {previewUrl ? (
              <AppImage
                src={previewUrl}
                alt={currentPhotoAlt || "User profile photo"}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Icon name="User" size={48} />
              </div>
            )}
          </div>

          {previewUrl && (
            <button
              onClick={handleRemovePhoto}
              className="absolute -top-2 -right-2 w-8 h-8 bg-destructive text-white rounded-full flex items-center justify-center"
            >
              <Icon name="X" size={16} />
            </button>
          )}
        </div>

        <div className="flex-1">
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`border-2 border-dashed rounded-lg p-6 text-center ${
              isDragging ? "border-primary bg-primary/5" : "border-border bg-muted/30"
            }`}
          >
            <Icon name="Upload" size={32} className="mx-auto mb-3" />
            <p className="text-sm font-medium mb-1">
              Drop your photo here or click to browse
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              JPG / PNG • Auto-compressed
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              className="hidden"
            />

            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              Choose File
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePhotoUpload;
